"use strict";

/**
 * WEB-4383 — "Fetch Latest" for the AML/CFT country lists (FATF, CBI/RBI).
 *
 * Backs the two developer settings screens that manage these lists:
 *
 *   - developer/settings/fatf_blacklist_countries  (FATFBlacklistCountriesController)
 *   - developer/settings/cbi_and_rbi_countries     (CbiAndRbiCountriesController)
 *
 * Both screens store their list as a developer_settings row holding a JSON array
 * of ISO 3166-1 numeric country codes. Until WEB-4315 the "latest" list existed
 * only as a hardcoded copy in each client, so a project seeded at setup could
 * never be brought back onto a newer FATF/OECD publication. The server now owns
 * the canonical list and exposes it; this service is the one place the portal
 * talks to those endpoints, so the two screens cannot drift apart the way the
 * hardcoded copies did.
 *
 * The flow the ticket asks for is deliberately two-step:
 *
 *   1. fetchLatest()    — GET the preview (never writes) and open the warning
 *                         modal showing what a refresh would add and remove.
 *   2. confirmRefresh() — POST the refresh with the `confirm` override flag,
 *                         which is the user's acknowledgement that their manual
 *                         entries may be overwritten.
 *
 * A refresh is a FULL REPLACE, not a merge: the project's list becomes exactly
 * the canonical list. Entries the user added by hand are dropped and canonical
 * entries the user deliberately removed come back. `preview.removed` is what the
 * modal warns on.
 *
 * @remarks The modal markup lives in
 *          resources/views/developer/settings/modals/modal_country_list_refresh.blade.php
 *          and binds to `ctrl.SSCountryListService.*`, so any controller using
 *          this service must expose it on itself under that name.
 */
class SSCountryListService {

    constructor(SSAlertService, SSHttpService, SSUtilService) {
        const self = this;

        self.SSAlertService = SSAlertService;
        self.SSHttpService = SSHttpService;
        self.SSUtilService = SSUtilService;

        /** List type keys, matching config/country_lists.php on the server. */
        self.LIST_TYPE_FATF = 'fatf';
        self.LIST_TYPE_CBI_RBI = 'cbi_rbi';

        self.MODAL_ID = '#countryListRefreshModal';

        self._reset();
    }

    /**
     * Clear everything, including the diff the modal renders.
     *
     * Only safe while the modal is closed — see _clearPending() for why the
     * dismiss path does not use this.
     *
     * @returns {void}
     */
    _reset() {
        const self = this;

        /** @type {object|null} The diff payload from the server, or null when idle. */
        self.preview = null;

        self._clearPending();
    }

    /**
     * Drop the pending action while leaving the rendered diff in place.
     *
     * The modal is a `.fade` modal, so it stays on screen for the length of the
     * hide transition. Nulling `preview` here would let a digest repaint it
     * blank mid-fade — counts empty and, since a null preview has no
     * `has_changes`, the "already matches the latest list" notice flashing up on
     * its way out. `preview` is therefore left for fetchLatest()'s _reset() to
     * clear before the next open. Dropping `listType` is what actually matters:
     * confirmRefresh() is a no-op without it, so a click landing on the closing
     * modal cannot still fire the write.
     *
     * @returns {void}
     */
    _clearPending() {
        const self = this;

        /** @type {string|null} The list type the open modal is about. */
        self.listType = null;

        /** @type {function|null} Invoked after a confirmed refresh lands. */
        self.callback = null;

        /** @type {boolean} True while the confirmed POST is in flight, to block a double submit. */
        self.refreshing = false;
    }

    /**
     * Base path of the WEB-4315 country-list endpoints for a list type.
     *
     * @param {string} listType 'fatf' or 'cbi_rbi'.
     * @returns {string} API path, relative to the sec/ API root.
     */
    _getBaseUrl(listType) {
        return 'sec/developer/settings/country-lists/' + listType;
    }

    /**
     * Step 1 — preview what a refresh would do, then open the warning modal.
     *
     * Reads only; nothing is written until the user confirms. The server returns
     * the same payload whether the list is currently configured or not, so this
     * also covers the case of a project switching the feature on for the first
     * time (`preview.configured` is false, `removed` is empty).
     *
     * @param {string} listType 'fatf' or 'cbi_rbi'.
     * @param {function} [callback] Invoked with the saved setting after a
     *        confirmed refresh. Not called when the user cancels.
     * @returns {void}
     */
    fetchLatest(listType, callback) {
        const self = this;

        self._reset();

        let url = self._getBaseUrl(listType) + '/preview';

        loading(true);
        self.SSHttpService.getAPIRequest(url).then(function (response) {
            loading(false);

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            let preview = response.data ? response.data.country_list : null;

            if (!preview) {
                self.SSAlertService.danger('Error!', 'Could not read the latest country list from the server.');
                return;
            }

            self.preview = preview;
            self.listType = listType;
            self.callback = callback;

            self.SSUtilService.showModal(self.MODAL_ID);
        });
    }

    /**
     * Step 2 — apply the canonical list, having obtained the user's confirmation.
     *
     * `confirm` is the server's override flag: without it the same POST returns
     * the preview and writes nothing, so this is the only call that can change
     * the project's list.
     *
     * @returns {void}
     */
    confirmRefresh() {
        const self = this;

        if (!self.listType || self.refreshing) {
            return;
        }

        let listType = self.listType;
        let callback = self.callback;

        let url = self._getBaseUrl(listType) + '/refresh';
        let params = {
            confirm: 1
        };

        self.refreshing = true;

        loading(true);
        self.SSHttpService.postAPIRequest(url, params).then(function (response) {
            loading(false);
            self.refreshing = false;

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            let applied = response.data && response.data.country_list
                ? response.data.country_list
                : null;

            // The server refuses to write without `confirm`, so a false `applied`
            // here means the flag did not survive the round trip. Say so rather
            // than reporting a success the project did not get.
            if (!applied || !applied.applied) {
                self.SSAlertService.danger('Error!', 'The country list was not updated. Please try again.');
                return;
            }

            self.hide();

            self.SSAlertService.success('Success!', (applied.label || 'Country list')
                + ' updated to the latest list.');

            if (callback && typeof callback === "function") {
                callback(response.data ? response.data.developer_settings : null);
            }
        });
    }

    /**
     * Dismiss the warning modal without changing anything — the ticket's
     * "on cancelled refresh, make no changes".
     *
     * @returns {void}
     */
    hide() {
        const self = this;

        self.SSUtilService.showModal(self.MODAL_ID, false);
        self._clearPending();
    }

    /**
     * Fetch the canonical ("latest") codes for a list type without writing.
     *
     * Used to seed a list the first time a project switches the feature on, so
     * the portal no longer carries its own hardcoded copy of the defaults — the
     * duplication WEB-4315 centralised on the server.
     *
     * @param {string} listType 'fatf' or 'cbi_rbi'.
     * @param {function} callback Invoked with an array of country codes. Not
     *        called when the request fails; the error is surfaced to the user.
     * @returns {void}
     */
    getLatestCodes(listType, callback) {
        const self = this;

        let url = self._getBaseUrl(listType) + '/preview';

        loading(true);
        self.SSHttpService.getAPIRequest(url).then(function (response) {
            loading(false);

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            let preview = response.data ? response.data.country_list : null;
            let latest = preview && preview.latest ? preview.latest : null;

            if (!latest || !latest.length) {
                self.SSAlertService.danger('Error!', 'Could not read the latest country list from the server.');
                return;
            }

            if (callback && typeof callback === "function") {
                callback(latest.map(country => country.country_code));
            }
        });
    }

}

SSCountryListService.$inject = [
    'SSAlertService',
    'SSHttpService',
    'SSUtilService'];
app.service('SSCountryListService', SSCountryListService);
